import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocketEvent } from '../context/SocketContext';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  FlaskConical, ShoppingCart, Plus, Save, X, Search,
  Calendar, Package, Loader, CheckCircle2, Clock, Ban,
  IndianRupee, Tag, Layers, AlertCircle, Bell,
} from 'lucide-react';
import ViewModeSwitcher, { useViewMode } from '../components/ViewModeSwitcher';

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const BUY_STATUS_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'ordered',   label: 'Ordered' },
  { key: 'received',  label: 'Received' },
  { key: 'cancelled', label: 'Cancelled' },
];
const SELL_STATUS_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'received',  label: 'Sold' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_STYLES = {
  pending:   { color: 'var(--color-warning)',  bg: 'var(--color-warning-soft)',  Icon: Clock },
  ordered:   { color: 'var(--color-info)',     bg: 'var(--color-info-soft)',     Icon: Package },
  received:  { color: 'var(--color-success)',  bg: 'var(--color-success-soft)', Icon: CheckCircle2 },
  cancelled: { color: 'var(--color-danger)',   bg: 'var(--color-danger-soft)',  Icon: Ban },
};
const SALE_PAYMENT_STYLES = {
  paid:    { color: 'var(--color-success)', bg: 'var(--color-success-soft)', label: 'Paid' },
  unpaid:  { color: 'var(--color-warning)', bg: 'var(--color-warning-soft)', label: 'Pending' },
  partial: { color: 'var(--color-info)',    bg: 'var(--color-info-soft)',    label: 'Partial' },
};

const SELL_CATEGORIES = ['oral-care','whitening','equipment','supplements','accessories','other'];
const UNITS = ['pcs','set','pair','box','unit','ml','gm','strip','bottle','tube'];

function StatusBadge({ status, isSell }) {
  if (isSell && status === 'received') {
    return <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:700, padding:'3px 9px', borderRadius:100, background:'var(--color-success-soft)', color:'var(--color-success)' }}><CheckCircle2 size={11} />Sold</span>;
  }
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:700, padding:'3px 9px', borderRadius:100, background:s.bg, color:s.color }}><s.Icon size={11} />{status.charAt(0).toUpperCase()+status.slice(1)}</span>;
}

function SalePaymentBadge({ status }) {
  const s = SALE_PAYMENT_STYLES[status] || SALE_PAYMENT_STYLES.paid;
  return <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:12, fontWeight:700, padding:'3px 9px', borderRadius:100, background:s.bg, color:s.color }}>{s.label}</span>;
}

function todayISO() { return new Date().toISOString().slice(0,10); }

const emptyBuy  = { itemName:'', quantity:1, unit:'pcs', orderDate:todayISO(), expectedDate:'', patientName:'', patientUID:'', patientId:'', labName:'', cost:'', notes:'' };
const emptySell = { itemName:'', quantity:1, unit:'pcs', orderDate:todayISO(), patientName:'', patientUID:'', patientId:'', cost:'', category:'oral-care', salePaymentMethod:'cash', salePaymentStatus:'paid', notes:'' };

// ── Shared patient search hook ─────────────────────────────────────────────────
function usePatientSearch() {
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow]       = useState(false);
  const debRef = useRef(null);
  const inputRef = useRef(null);
  const dropRef  = useRef(null);

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); setShow(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/patients/search', { params: { q: q.trim() } });
      setResults(res.data.patients || []);
      setShow(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => search(query), 280);
    return () => clearTimeout(debRef.current);
  }, [query, search]);

  useEffect(() => {
    function h(e) {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) setShow(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function clear() { setQuery(''); setResults([]); setShow(false); }

  return { query, setQuery, results, loading, show, setShow, inputRef, dropRef, clear };
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function LabPage() {
  const { showToast } = useToast();
  const { user } = useAuth();

  // Which tab is active
  const [tab, setTab] = useState('buy'); // 'buy' | 'sell'

  // Shared list state
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({ pending:0, ordered:0, received:0, cancelled:0, totalCost:0 });
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [limit, setLimit]   = useState(25);
  const [selectedDate, setSelectedDate] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('none');
  const [listMode, setListMode] = useViewMode('lab_view', 'list');
  const [showStats, setShowStats] = useState(() => localStorage.getItem('lab_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('lab_showStats', !v); return !v; });

  // Form visibility
  const [showForm, setShowForm] = useState(false);
  const [buyForm,  setBuyForm]  = useState(emptyBuy);
  const [sellForm, setSellForm] = useState(emptySell);
  const [saving, setSaving] = useState(false);

  // Edit
  const [editingId,   setEditingId]   = useState(null);
  const [editForm,    setEditForm]    = useState({});
  const [editSaving,  setEditSaving]  = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  // Patient search for buy/sell forms
  const buyPat  = usePatientSearch();
  const sellPat = usePatientSearch();

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/lab', { params: { type: tab, date: selectedDate, status: statusFilter, search, page, limit } });
      setOrders(res.data.orders);
      setTotals(res.data.summaryTotals);
      setTotal(res.data.total);
      setAvailableDates(res.data.availableDates || []);
    } catch { showToast('Could not load LAB records.', 'error'); }
    finally { setLoading(false); }
  }, [tab, selectedDate, statusFilter, search, page, limit, showToast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setStatusFilter('all'); setSelectedDate('all'); setShowForm(false); }, [tab]);

  useSocketEvent('lab:order-updated', () => load());

  function pickPatient(p, which) {
    const setter = which === 'buy' ? setBuyForm : setSellForm;
    setter((f) => ({ ...f, patientName: p.name, patientUID: p.patientUID, patientId: p._id }));
    if (which === 'buy') buyPat.clear(); else sellPat.clear();
  }
  function clearPatient(which) {
    const setter = which === 'buy' ? setBuyForm : setSellForm;
    setter((f) => ({ ...f, patientName: '', patientUID: '', patientId: '' }));
  }

  // ── CRUD ──────────────────────────────────────────────────────────────────
  async function handleCreate(e) {
    e.preventDefault();
    const form = tab === 'buy' ? buyForm : sellForm;
    if (!form.itemName.trim()) { showToast('Item name is required.', 'error'); return; }
    setSaving(true);
    try {
      const payload = tab === 'buy'
        ? { type:'buy', ...buyForm,  quantity: Number(buyForm.quantity),  cost: Number(buyForm.cost)  || 0 }
        : { type:'sell',...sellForm, quantity: Number(sellForm.quantity), cost: Number(sellForm.cost) || 0 };
      const res = await api.post('/lab', payload);
      showToast(tab === 'buy' ? 'Lab order created.' : 'Sale recorded.', 'success');
      setOrders((prev) => [res.data.order, ...prev]);
      if (tab === 'buy') { setBuyForm(emptyBuy); buyPat.clear(); }
      else               { setSellForm(emptySell); sellPat.clear(); }
      setShowForm(false);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Could not save.', 'error'); }
    finally { setSaving(false); }
  }

  async function handleUpdate(id) {
    setEditSaving(true);
    try {
      const res = await api.put(`/lab/${id}`, { ...editForm, quantity: Number(editForm.quantity), cost: Number(editForm.cost) || 0 });
      showToast('Updated.', 'success');
      setOrders((prev) => prev.map((o) => o._id === id ? res.data.order : o));
      setEditingId(null);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Could not update.', 'error'); }
    finally { setEditSaving(false); }
  }

  async function handleCancel() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/lab/${deleteTarget._id}`);
      showToast('Cancelled.', 'success');
      setOrders((prev) => prev.map((o) => o._id === deleteTarget._id ? res.data.order : o));
      setDeleteTarget(null);
      load();
    } catch (err) { showToast(err.response?.data?.message || 'Could not cancel.', 'error'); }
    finally { setDeleting(false); }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const statusOptions = tab === 'buy' ? BUY_STATUS_OPTIONS : SELL_STATUS_OPTIONS;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout title="LAB" subtitle={tab === 'buy' ? 'Lab material orders and purchases from suppliers' : 'Products sold to patients at the clinic'}>

      {/* ── Buy / Sell tabs ──────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:0, marginBottom:24, border:'1px solid var(--color-border)', borderRadius:12, overflow:'hidden', width:'fit-content' }}>
        <TabBtn active={tab==='buy'}  onClick={() => setTab('buy')}  icon={FlaskConical} label="Buy (Orders)" sub="Materials from lab/supplier" />
        <TabBtn active={tab==='sell'} onClick={() => setTab('sell')} icon={ShoppingCart} label="Sell (Retail)" sub="Products sold to patients" />
      </div>

      {/* ── Summary cards ────────────────────────────────────────────────── */}
      {user?.isAdmin && (
        <div style={{ marginBottom:20 }}>
          <button className="btn btn-outline btn-sm" style={{ marginBottom: showStats?10:0, display:'flex', alignItems:'center', gap:6 }} onClick={toggleStats}>
            {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
          </button>
          {showStats && <div className="stat-grid">
        {tab === 'buy' ? <>
          <SCard label="Pending"   value={totals.pending}  color="warning" Icon={Clock} />
          <SCard label="Ordered"   value={totals.ordered}  color="info"    Icon={Package} />
          <SCard label="Received"  value={totals.received} color="success" Icon={CheckCircle2} />
          <SCard label="Total Spend" value={`₹${fmt(totals.totalCost)}`} color="primary" Icon={IndianRupee} />
        </> : <>
          <SCard label="Total Sales"     value={totals.received}  color="success" Icon={CheckCircle2} />
          <SCard label="Cancelled"       value={totals.cancelled} color="danger"  Icon={Ban} />
          <SCard label="Revenue"         value={`₹${fmt(totals.totalCost)}`} color="primary" Icon={IndianRupee} />
          <SCard label="Items in period" value={total}            color="info"    Icon={Tag} />
        </>}
          </div>}
        </div>
      )}

      {/* ── Expected date reminders (buy tab only) ──────────────────────── */}
      {tab === 'buy' && (() => {
        const today = todayISO();
        const overdue  = orders.filter(o => o.expectedDate && o.expectedDate < today  && o.status === 'ordered');
        const dueToday = orders.filter(o => o.expectedDate && o.expectedDate === today && o.status === 'ordered');
        const dueSoon  = orders.filter(o => o.expectedDate && o.expectedDate > today   && o.expectedDate <= new Date(Date.now()+3*86400000).toISOString().slice(0,10) && o.status === 'ordered');
        if (!overdue.length && !dueToday.length && !dueSoon.length) return null;
        return (
          <div style={{ marginBottom:16 }}>
            {overdue.length > 0 && (
              <div style={{ padding:'10px 14px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, marginBottom:8, display:'flex', alignItems:'flex-start', gap:10 }}>
                <AlertCircle size={15} color="#dc2626" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#dc2626' }}>Overdue — {overdue.length} order{overdue.length>1?'s':''} past expected date</div>
                  {overdue.map(o => (
                    <div key={o._id} style={{ fontSize:12, color:'#b91c1c', marginTop:3 }}>
                      {o.itemName} — {o.patientName} — Expected: {o.expectedDate} — Lab: {o.labName||'—'}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dueToday.length > 0 && (
              <div style={{ padding:'10px 14px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:8, marginBottom:8, display:'flex', alignItems:'flex-start', gap:10 }}>
                <Clock size={15} color="#d97706" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#d97706' }}>Due Today — {dueToday.length} order{dueToday.length>1?'s':''} expected today</div>
                  {dueToday.map(o => (
                    <div key={o._id} style={{ fontSize:12, color:'#b45309', marginTop:3 }}>
                      {o.itemName} — {o.patientName} — Lab: {o.labName||'—'}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dueSoon.length > 0 && (
              <div style={{ padding:'10px 14px', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, marginBottom:8, display:'flex', alignItems:'flex-start', gap:10 }}>
                <Bell size={15} color="#2563eb" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:'#2563eb' }}>Due in 1–3 days — {dueSoon.length} order{dueSoon.length>1?'s':''} coming up</div>
                  {dueSoon.map(o => (
                    <div key={o._id} style={{ fontSize:12, color:'#1d4ed8', marginTop:3 }}>
                      {o.itemName} — {o.patientName} — Expected: {o.expectedDate} — Lab: {o.labName||'—'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Calendar size={15} color="var(--color-ink-faint)" />
          <select className="select" style={{ minWidth:160, fontWeight:600 }} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>
            <option value="all">All dates</option>
            {availableDates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div style={{ position:'relative', flex:'1 1 200px' }}>
          <Search size={15} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)' }} />
          <input className="input" style={{ paddingLeft:32 }} placeholder={tab==='buy' ? 'Search item, patient, lab…' : 'Search item, patient, category…'} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--color-ink-soft)', whiteSpace:'nowrap' }}>
          Show
          <select className="select" style={{ width:72, fontWeight:600, padding:'8px 6px' }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          per page
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          <Plus size={15} />{showForm ? 'Cancel' : (tab==='buy' ? 'New Order' : 'Record Sale')}
        </button>
      </div>

      {/* ── Status filter tabs ───────────────────────────────────────────── */}
      <div className="page-toolbar" style={{ display:'flex', alignItems:'center', gap:12, marginBottom:18, flexWrap:'wrap' }}>
        <ViewModeSwitcher mode={listMode} onChange={setListMode} />
        <div style={{ display:'flex', gap:4, background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:10, padding:4 }}>
          {statusOptions.map(({ key, label }) => {
            const count = key === 'all' ? orders.length : orders.filter((o) => o.status === key).length;
            const active = statusFilter === key;
            return (
              <button key={key} onClick={() => setStatusFilter(key)} className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}>
                {label}
                <span style={{ fontSize:11, fontWeight:700, background:active?'rgba(255,255,255,0.25)':'var(--color-primary-soft)', color:active?'white':'var(--color-primary)', borderRadius:100, padding:'1px 7px', marginLeft:6 }}>{count}</span>
              </button>
            );
          })}
        </div>
        {/* Group By */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Layers size={14} color="var(--color-ink-faint)"/>
          <select className="select" style={{ minWidth:150, fontWeight:600 }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="none">No grouping</option>
            <option value="date">Group by Date</option>
            <option value="status">Group by Status</option>
            <option value="patient">Group by Patient</option>
            {tab === 'buy'  && <option value="lab">Group by Lab</option>}
            {tab === 'sell' && <option value="category">Group by Category</option>}
          </select>
        </div>
      </div>

      {/* ── Create form ──────────────────────────────────────────────────── */}
      {showForm && (
        <div className="card" style={{ padding:22, marginBottom:20 }}>
          <h3 style={{ margin:'0 0 18px', fontSize:15, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
            {tab==='buy' ? <><FlaskConical size={18} color="var(--color-primary)" /> New Lab Order (Buy)</> : <><ShoppingCart size={18} color="var(--color-success)" /> Record Retail Sale</>}
          </h3>
          <form onSubmit={handleCreate}>
            {tab === 'buy'
              ? <BuyForm form={buyForm} setForm={setBuyForm} pat={buyPat} onPickPatient={(p) => pickPatient(p,'buy')} onClearPatient={() => clearPatient('buy')} />
              : <SellForm form={sellForm} setForm={setSellForm} pat={sellPat} onPickPatient={(p) => pickPatient(p,'sell')} onClearPatient={() => clearPatient('sell')} />
            }
            <div style={{ display:'flex', gap:10, marginTop:18 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}><Save size={15} />{saving ? 'Saving…' : (tab==='buy'?'Create Order':'Record Sale')}</button>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Orders list ──────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ display:'grid', gap:10 }}>{[1,2,3].map((i) => <div key={i} className="skeleton" style={{ height:68, borderRadius:8 }} />)}</div>
      ) : orders.length === 0 ? (
        <div className="card" style={{ padding:48, textAlign:'center', color:'var(--color-ink-faint)' }}>
          {tab==='buy' ? <FlaskConical size={36} style={{ marginBottom:12, opacity:0.5 }} /> : <ShoppingCart size={36} style={{ marginBottom:12, opacity:0.5 }} />}
          <p style={{ margin:0 }}>{tab==='buy' ? 'No lab orders found.' : 'No sales recorded yet.'}</p>
        </div>
      ) : (
        (() => {
          const getGroupKey = o => {
            if (groupBy === 'date')     return o.orderDate || 'No date';
            if (groupBy === 'status')   return o.status ? o.status.charAt(0).toUpperCase()+o.status.slice(1) : 'Unknown';
            if (groupBy === 'patient')  return o.patientName ? `${o.patientName}${o.patientUID ? ` (${o.patientUID})` : ''}` : 'No patient';
            if (groupBy === 'lab')      return o.labName || 'No lab';
            if (groupBy === 'category') return o.category || 'Uncategorized';
            return '__all__';
          };
          const groups = {};
          orders.forEach(o => { const k = getGroupKey(o); if (!groups[k]) groups[k] = []; groups[k].push(o); });

          return (
            <div style={{ display:'grid', gap: groupBy === 'none' ? 10 : 16 }}>
              {Object.entries(groups).map(([groupKey, groupOrders]) => (
                <div key={groupKey}>
                  {groupBy !== 'none' && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, padding:'6px 12px', background:'var(--color-surface)', borderRadius:8, border:'1px solid var(--color-border)' }}>
                      <Layers size={13} color="var(--color-ink-faint)"/>
                      <span style={{ fontWeight:700, fontSize:13 }}>{groupKey}</span>
                      <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {groupOrders.length} record{groupOrders.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {listMode === 'compact' ? (
                    <div className="card" style={{ overflow:'hidden' }}>
                      {groupOrders.map((o,ci) => (
                        <div key={o._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 14px', borderTop:ci===0?'none':'1px solid var(--color-border)', background:ci%2===0?'transparent':'var(--color-surface)' }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <span style={{ fontWeight:700, fontSize:13 }}>{o.itemName}</span>
                            <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{o.type==='sell'?o.patientName:o.labName}</span>
                          </div>
                          <span style={{ fontSize:12, fontWeight:700, color:'var(--color-primary)' }}>₹{o.cost||0}</span>
                          <span style={{ fontSize:11, color:'var(--color-ink-faint)' }}>Qty:{o.quantity}</span>
                          <button className="btn btn-icon btn-ghost btn-sm" onClick={()=>{setEditingId(o._id);setEditForm({...o});}}><Save size={13}/></button>
                        </div>
                      ))}
                    </div>
                  ) : listMode === 'grid' ? (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:10 }}>
                      {groupOrders.map(o => (
                        <div key={o._id} className="card" style={{ padding:12, display:'flex', flexDirection:'column', gap:5 }}>
                          <div style={{ fontWeight:700, fontSize:13 }}>{o.itemName}</div>
                          <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{o.type==='sell'?'To: '+o.patientName:'Lab: '+o.labName}</div>
                          {o.expectedDate && <div style={{ fontSize:11, color:'var(--color-warning)', fontWeight:600 }}>Due: {o.expectedDate}</div>}
                          <div style={{ fontWeight:700, color:'var(--color-primary)', marginTop:'auto', paddingTop:4 }}>₹{o.cost||0} · Qty {o.quantity}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                  <div style={{ display:'grid', gap:10 }}>
                    {groupOrders.map((o) => (
                      <div key={o._id} className="card" style={{ padding:16 }}>
                        {editingId === o._id
                          ? <EditRow order={o} form={editForm} setForm={setEditForm} saving={editSaving} onSave={() => handleUpdate(o._id)} onCancel={() => setEditingId(null)} isSell={o.type==='sell'} />
                          : <OrderRow o={o} isSell={o.type==='sell'} onEdit={() => { setEditingId(o._id); setEditForm({...o}); }} onDelete={() => setDeleteTarget(o)} />
                        }
                      </div>
                    ))}
                  </div>)}
                </div>
              ))}
            </div>
          );
        })()
      )}

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {!loading && total > 0 && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, flexWrap:'wrap', gap:10 }}>
          <div style={{ fontSize:13, color:'var(--color-ink-soft)' }}>
            Showing <strong>{((page-1)*limit)+1}</strong>–<strong>{Math.min(page*limit,total)}</strong> of <strong>{total}</strong>
          </div>
          <div style={{ display:'flex', gap:4 }}>
            <button className="btn btn-outline btn-sm" disabled={page===1} onClick={() => setPage(1)}>«</button>
            <button className="btn btn-outline btn-sm" disabled={page===1} onClick={() => setPage((p)=>p-1)}>‹</button>
            <span style={{ padding:'0 12px', fontSize:13, fontWeight:600, display:'flex', alignItems:'center' }}>Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" disabled={page===totalPages} onClick={() => setPage((p)=>p+1)}>›</button>
            <button className="btn btn-outline btn-sm" disabled={page===totalPages} onClick={() => setPage(totalPages)}>»</button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Cancel "${deleteTarget?.itemName}"?`}
        message="This entry will be marked cancelled and kept for records."
        confirmLabel={deleting ? 'Cancelling…' : 'Cancel Entry'}
        danger onConfirm={handleCancel} onCancel={() => setDeleteTarget(null)}
      />
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </AppLayout>
  );
}

// ── Tab button ────────────────────────────────────────────────────────────────
function TabBtn({ active, onClick, icon:Icon, label, sub }) {
  return (
    <button type="button" onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:10, padding:'12px 20px',
      border:'none', cursor:'pointer', minWidth:200,
      background: active ? 'var(--color-primary)' : 'var(--color-surface)',
      color: active ? 'white' : 'var(--color-ink-soft)',
      transition:'background 0.15s, color 0.15s',
    }}>
      <Icon size={18} />
      <div style={{ textAlign:'left' }}>
        <div style={{ fontWeight:700, fontSize:14 }}>{label}</div>
        <div style={{ fontSize:11, opacity:0.75 }}>{sub}</div>
      </div>
    </button>
  );
}

// ── Buy form ──────────────────────────────────────────────────────────────────
function BuyForm({ form, setForm, pat, onPickPatient, onClearPatient }) {
  const f = (field) => (e) => setForm((s) => ({ ...s, [field]: e.target.value }));
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:14 }}>
        <F label="Item / Material Name *" style={{ gridColumn:'span 2' }}>
          <input className="input" value={form.itemName} onChange={f('itemName')} placeholder="e.g. PFM Crown, Composite Kit, Impression Material" required />
        </F>
        <F label="Qty *"><input className="input" type="number" min="1" value={form.quantity} onChange={f('quantity')} /></F>
        <F label="Unit">
          <select className="select" value={form.unit} onChange={f('unit')}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </F>
        <F label="Order Date"><input className="input" type="date" value={form.orderDate} onChange={f('orderDate')} /></F>
        <F label="Expected Delivery"><input className="input" type="date" value={form.expectedDate} onChange={f('expectedDate')} /></F>
        <F label="Lab / Supplier Name"><input className="input" value={form.labName} onChange={f('labName')} placeholder="e.g. Smile Dental Lab" /></F>
        <F label="Cost to Clinic (₹)"><input className="input" type="number" min="0" value={form.cost} onChange={f('cost')} placeholder="0" /></F>
      </div>
      <PatientPicker pat={pat} form={form} onPick={onPickPatient} onClear={onClearPatient} label="Patient this order is for (optional)" />
      <F label="Notes" style={{ marginTop:12 }}>
        <input className="input" value={form.notes} onChange={f('notes')} placeholder="Shade, specifications, special instructions…" />
      </F>
    </div>
  );
}

// ── Sell form ─────────────────────────────────────────────────────────────────
function SellForm({ form, setForm, pat, onPickPatient, onClearPatient }) {
  const f = (field) => (e) => setForm((s) => ({ ...s, [field]: e.target.value }));
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:14 }}>
        <F label="Product Name *" style={{ gridColumn:'span 2' }}>
          <input className="input" value={form.itemName} onChange={f('itemName')} placeholder="e.g. Toothbrush, Mouthwash 500ml, Whitening Kit" required />
        </F>
        <F label="Qty *"><input className="input" type="number" min="1" value={form.quantity} onChange={f('quantity')} /></F>
        <F label="Unit">
          <select className="select" value={form.unit} onChange={f('unit')}>
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </F>
        <F label="Category">
          <select className="select" value={form.category} onChange={f('category')}>
            {SELL_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('-',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
          </select>
        </F>
        <F label="Sale Date"><input className="input" type="date" value={form.orderDate} onChange={f('orderDate')} /></F>
        <F label="Selling Price (₹)"><input className="input" type="number" min="0" value={form.cost} onChange={f('cost')} placeholder="0" /></F>
        <F label="Payment Method">
          <select className="select" value={form.salePaymentMethod} onChange={f('salePaymentMethod')}>
            <option value="cash">Cash</option><option value="card">Card</option>
            <option value="upi">UPI</option><option value="other">Other</option>
          </select>
        </F>
        <F label="Payment Status">
          <select className="select" value={form.salePaymentStatus} onChange={f('salePaymentStatus')}>
            <option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="partial">Partial</option>
          </select>
        </F>
      </div>
      <PatientPicker pat={pat} form={form} onPick={onPickPatient} onClear={onClearPatient} label="Sold to patient (optional)" />
      <F label="Notes" style={{ marginTop:12 }}>
        <input className="input" value={form.notes} onChange={f('notes')} placeholder="Batch number, any remarks…" />
      </F>
    </div>
  );
}

// ── Patient picker shared ─────────────────────────────────────────────────────
function PatientPicker({ pat, form, onPick, onClear, label }) {
  return (
    <div>
      <label className="label">{label}</label>
      {form.patientName ? (
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--color-primary-soft)', borderRadius:8, fontSize:13 }}>
          <span style={{ fontWeight:700, color:'var(--color-primary)' }}>{form.patientName}</span>
          <span style={{ color:'var(--color-primary)', opacity:0.7, fontFamily:'var(--font-mono)', fontSize:12 }}>{form.patientUID}</span>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClear} style={{ marginLeft:'auto' }}><X size={14} /></button>
        </div>
      ) : (
        <div style={{ position:'relative' }} ref={pat.inputRef}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', pointerEvents:'none' }} />
          <input className="input" style={{ paddingLeft:30 }} value={pat.query}
            onChange={(e) => pat.setQuery(e.target.value)} placeholder="Search by name or patient ID…" autoComplete="off" />
          {pat.loading && <Loader size={13} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', color:'var(--color-ink-faint)', animation:'spin 1s linear infinite' }} />}
          {pat.show && pat.results.length > 0 && (
            <div ref={pat.dropRef} className="card" style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:200, padding:0, maxHeight:200, overflowY:'auto', boxShadow:'var(--shadow-lg)' }}>
              {pat.results.map((p) => (
                <button key={p._id} type="button" onClick={() => onPick(p)}
                  style={{ width:'100%', textAlign:'left', padding:'10px 14px', border:'none', background:'transparent', cursor:'pointer', borderBottom:'1px solid var(--color-border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background='var(--color-primary-soft)'}
                  onMouseLeave={(e) => e.currentTarget.style.background='transparent'}>
                  <div style={{ fontWeight:700, fontSize:13 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:'var(--color-ink-faint)', fontFamily:'var(--font-mono)' }}>{p.patientUID}{p.phone && ` · ${p.phone}`}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Row display ───────────────────────────────────────────────────────────────
function OrderRow({ o, isSell, onEdit, onDelete }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10 }}>
      <div>
        <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
          {isSell ? <ShoppingCart size={14} color="var(--color-success)" /> : <FlaskConical size={14} color="var(--color-primary)" />}
          {o.itemName}
          <span style={{ fontSize:13, fontWeight:400, color:'var(--color-ink-soft)' }}>{o.quantity} {o.unit}</span>
          {isSell && o.category && <span style={{ fontSize:11, padding:'2px 7px', borderRadius:100, background:'var(--color-info-soft)', color:'var(--color-info)', fontWeight:600 }}>{o.category}</span>}
        </div>
        <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
          <span>{isSell ? 'Sale:' : 'Order:'} {o.orderDate}</span>
          {!isSell && o.expectedDate && <span>Expected: {o.expectedDate}</span>}
          {o.patientName && <span>Patient: <strong>{o.patientName}</strong> ({o.patientUID})</span>}
          {!isSell && o.labName && <span>Lab: {o.labName}</span>}
          {o.cost > 0 && <span>{isSell ? 'Price' : 'Cost'}: <strong>₹{o.cost.toLocaleString('en-IN')}</strong></span>}
          {isSell && <span>Payment: {o.salePaymentMethod}</span>}
          {o.notes && <span style={{ fontStyle:'italic' }}>{o.notes}</span>}
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <StatusBadge status={o.status} isSell={isSell} />
        {isSell && <SalePaymentBadge status={o.salePaymentStatus || 'paid'} />}
        {o.status !== 'cancelled' && (
          <>
            <button className="btn btn-outline btn-sm" onClick={onEdit}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={onDelete}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Inline edit row ───────────────────────────────────────────────────────────
function EditRow({ form, setForm, saving, onSave, onCancel, isSell }) {
  const ef = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:12 }}>
        <F label="Item Name"><input className="input" value={form.itemName||''} onChange={ef('itemName')} /></F>
        <F label="Qty"><input className="input" type="number" min="1" value={form.quantity||''} onChange={ef('quantity')} /></F>
        {!isSell && (
          <F label="Status">
            <select className="select" value={form.status||'pending'} onChange={ef('status')}>
              <option value="pending">Pending</option><option value="ordered">Ordered</option>
              <option value="received">Received</option><option value="cancelled">Cancelled</option>
            </select>
          </F>
        )}
        {isSell && (
          <F label="Payment Status">
            <select className="select" value={form.salePaymentStatus||'paid'} onChange={ef('salePaymentStatus')}>
              <option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="partial">Partial</option>
            </select>
          </F>
        )}
        {!isSell && <F label="Expected Date"><input className="input" type="date" value={form.expectedDate||''} onChange={ef('expectedDate')} /></F>}
        {!isSell && <F label="Lab Name"><input className="input" value={form.labName||''} onChange={ef('labName')} /></F>}
        <F label={isSell ? 'Price (₹)' : 'Cost (₹)'}><input className="input" type="number" value={form.cost||''} onChange={ef('cost')} /></F>
      </div>
      <F label="Notes"><input className="input" value={form.notes||''} onChange={ef('notes')} /></F>
      <div style={{ display:'flex', gap:10, marginTop:12 }}>
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}><Save size={13} />{saving?'Saving…':'Save'}</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Utils ─────────────────────────────────────────────────────────────────────
function fmt(n) { return (n||0).toLocaleString('en-IN',{maximumFractionDigits:0}); }

function SCard({ label, value, color, Icon }) {
  return (
    <div className="stat-card">
      <div className="stat-card-icon" style={{ background:`var(--color-${color}-soft)` }}>
        <Icon size={18} color={`var(--color-${color})`} />
      </div>
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
    </div>
  );
}

function F({ label, children, style }) {
  return (
    <div className="field" style={{ marginBottom:0, ...style }}>
      <label className="label">{label}</label>{children}
    </div>
  );
}
